import React, { useState } from 'react';
import styled from 'styled-components';
import { useLanguage } from '../context/LanguageContext';

// Styled components for the User Manual
const ManualSection = styled.section`
  padding: 6rem 2rem;
  background-color: ${props => props.theme.colors.backgroundAlt};
`;

const SectionInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 3rem;
  color: ${props => props.theme.colors.primary};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const ManualIntro = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  
  p {
    font-size: 1.1rem;
    line-height: 1.6;
    color: ${props => props.theme.colors.textLight};
  }
`;

const AccordionContainer = styled.div`
  margin-bottom: 2rem;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.medium};
  background-color: white;
`;

const AccordionItem = styled.div`
  border-bottom: 1px solid ${props => props.theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const AccordionHeader = styled.button`
  width: 100%;
  text-align: left;
  padding: 1.25rem;
  background-color: ${props => props.isActive ? props.theme.colors.primaryLight : 'white'};
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: ${props => props.isActive ? props.theme.colors.primaryLight : props.theme.colors.backgroundAlt};
  }
  
  h3 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 600;
    color: ${props => props.isActive ? props.theme.colors.primary : props.theme.colors.text};
  }
  
  span {
    transition: transform 0.3s ease;
    transform: ${props => props.isActive ? 'rotate(180deg)' : 'rotate(0)'};
    font-size: 1.5rem;
    color: ${props => props.isActive ? props.theme.colors.primary : props.theme.colors.textLight};
  }
`;

const AccordionContent = styled.div`
  padding: ${props => props.isActive ? '1.25rem' : '0 1.25rem'};
  max-height: ${props => props.isActive ? '2000px' : '0'};
  overflow: hidden;
  transition: all 0.5s ease;
  
  p {
    margin: 0 0 1rem;
    line-height: 1.6;
  }
  
  ul {
    margin: 0 0 1rem;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }
  
  strong {
    color: ${props => props.theme.colors.primary};
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const FeatureCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: ${props => props.theme.shadows.small};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.medium};
  }
  
  h4 {
    font-size: 1.2rem;
    font-weight: 600;
    margin-top: 0;
    margin-bottom: 1rem;
    color: ${props => props.theme.colors.primary};
  }
  
  p {
    margin: 0;
    color: ${props => props.theme.colors.text};
    line-height: 1.5;
  }
`;

const ImageContainer = styled.div`
  margin: 2rem 0;
  text-align: center;
  
  img {
    max-width: 100%;
    border-radius: 8px;
    box-shadow: ${props => props.theme.shadows.medium};
  }
  
  figcaption {
    margin-top: 0.75rem;
    font-size: 0.9rem;
    color: ${props => props.theme.colors.textLight};
    font-style: italic;
  }
`;

// User Manual Component
const UserManual = () => {
  const { translate } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Toggle accordion item
  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };
  
  // Manual sections based on WeatherTellerInfoPage content
  const manualSections = [
    {
      title: translate('manual.section1.title', 'Getting Started with WeatherTeller'),
      content: (
        <>
          <p>{translate('manual.section1.intro', 'WeatherTeller is a community-driven weather reporting app that allows you to submit and view real-time weather conditions through an interactive map interface. Here\'s how to get started:')}</p>
          <ul>
            <li>{translate('manual.section1.step1', 'Create your profile by tapping the profile icon in the top right corner')}</li>
            <li>{translate('manual.section1.step2', 'Enable location services to see weather reports near you')}</li>
            <li>{translate('manual.section1.step3', 'Explore the map to view community weather reports')}</li>
            <li>{translate('manual.section1.step4', 'Submit your first weather report by tapping the "+" button')}</li>
          </ul>
          <ImageContainer>
            <img src="/screenshot1.png" alt={translate('manual.section1.screenshot1Alt', 'WeatherTeller App Interface')} />
            <figcaption>{translate('manual.section1.screenshot1Caption', 'The WeatherTeller main interface showing the map view with weather reports')}</figcaption>
          </ImageContainer>
        </>
      )
    },
    {
      title: translate('manual.section2.title', 'Submitting Weather Reports'),
      content: (
        <>
          <p>{translate('manual.section2.intro', 'Sharing your local weather conditions with the community is easy and fun:')}</p>
          <ul>
            <li>{translate('manual.section2.step1', 'Tap the screen to open the weather report form')}</li>
            <li>{translate('manual.section2.step2', 'Select the appropriate emoji that represents the current weather condition')}</li>
            <li>{translate('manual.section2.step3', 'Add an optional text description for more details')}</li>
            <li>{translate('manual.section2.step4', 'Include photos if you wish to share visual evidence')}</li>
            <li>{translate('manual.section2.step5', 'Submit your report to share with the community')}</li>
          </ul>
          <p>{translate('manual.section2.note', 'Note: All reports are associated with your current location. For accurate reporting, ensure your location services are enabled.')}</p>
          <ImageContainer>
            <img src="/screenshot2.png" alt={translate('manual.section2.screenshot2Alt', 'Weather Report Submission Form')} />
            <figcaption>{translate('manual.section2.screenshot2Caption', 'The weather report submission form showing emoji selection and optional text input')}</figcaption>
          </ImageContainer>
        </>
      )
    },
    {
      title: translate('manual.section3.title', 'Exploring Community Reports'),
      content: (
        <>
          <p>{translate('manual.section3.intro', 'Discover weather conditions reported by other community members:')}</p>
          <ul>
            <li>{translate('manual.section3.step1', 'Navigate the map to different locations by dragging and zooming')}</li>
            <li>{translate('manual.section3.step2', 'Tap on weather emojis to view detailed reports')}</li>
            <li>{translate('manual.section3.step3', 'Use the "Ask Near Me" feature to find reports in your vicinity')}</li>
            <li>{translate('manual.section3.step4', 'Validate reports by giving thumbs up to accurate information')}</li>
          </ul>
          <p>{translate('manual.section3.tip', 'Tip: The size of emoji icons indicates the number of validations a report has received from the community.')}</p>
        </>
      )
    },
    {
      title: translate('manual.section4.title', 'Profile Management'),
      content: (
        <>
          <p>{translate('manual.section4.intro', 'Customize your WeatherTeller experience:')}</p>
          <ul>
            <li>{translate('manual.section4.step1', 'Create multiple profiles for different reporting styles')}</li>
            <li>{translate('manual.section4.step2', 'Select your preferred interface layout')}</li>
            <li>{translate('manual.section4.step3', 'Choose startup behavior (map view, report form, etc.)')}</li>
            <li>{translate('manual.section4.step4', 'Configure notification preferences')}</li>
          </ul>
          <p>{translate('manual.section4.note', 'Your profiles are stored securely and can be synchronized across devices when signed in.')}</p>
        </>
      )
    },
    {
      title: translate('manual.section5.title', 'Advanced Features'),
      content: (
        <>
          <p>{translate('manual.section5.intro', 'Discover WeatherTeller\'s powerful capabilities:')}</p>
          <FeatureGrid>
            <FeatureCard>
              <h4>{translate('manual.section5.feature1.title', 'Automatic Reports')}</h4>
              <p>{translate('manual.section5.feature1.desc', 'Configure the app to automatically generate weather reports for specific locations on a schedule.')}</p>
            </FeatureCard>
            <FeatureCard>
              <h4>{translate('manual.section5.feature2.title', 'Community Metrics')}</h4>
              <p>{translate('manual.section5.feature2.desc', 'View statistics about community engagement, report accuracy, and top contributors.')}</p>
            </FeatureCard>
            <FeatureCard>
              <h4>{translate('manual.section5.feature3.title', 'Points of Interest')}</h4>
              <p>{translate('manual.section5.feature3.desc', 'Report and discover non-weather related points of interest that may be relevant to travelers.')}</p>
            </FeatureCard>
            <FeatureCard>
              <h4>{translate('manual.section5.feature4.title', 'Feature Voting')}</h4>
              <p>{translate('manual.section5.feature4.desc', 'Vote on upcoming features to help shape the future development of WeatherTeller.')}</p>
            </FeatureCard>
          </FeatureGrid>
        </>
      )
    },
  ];
  
  return (
    <ManualSection id="manual">
      <SectionInner>
        <SectionTitle>{translate('manual.title', 'User Manual')}</SectionTitle>
        <ManualIntro>
          <p>{translate('manual.intro', 'Learn how to make the most of WeatherTeller\'s features with our comprehensive user guide. Whether you\'re a new user or looking to discover advanced features, this manual will help you navigate the app effectively.')}</p>
        </ManualIntro>
        
        {manualSections.map((section, index) => (
          <AccordionContainer key={index}>
            <AccordionItem>
              <AccordionHeader 
                isActive={activeIndex === index}
                onClick={() => toggleAccordion(index)}
              >
                <h3>{section.title}</h3>
                <span>{activeIndex === index ? '▲' : '▼'}</span>
              </AccordionHeader>
              <AccordionContent isActive={activeIndex === index}>
                {section.content}
              </AccordionContent>
            </AccordionItem>
          </AccordionContainer>
        ))}
      </SectionInner>
    </ManualSection>
  );
};

export default UserManual;